import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AdvanceQualificationMethod,
  LendingPaymentSource,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import BackUpPaymentsBottomSheet from 'features/advance/components/BackUpPaymentOptionsBottomSheet/BackUpPaymentOptionsBottomSheet';
import useBackupPayments from 'features/advance/hooks/useBackupPayments';
import { TabaPayEntryFlowType } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const LinkDebitCard = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'linkDebitCard',
  });
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const {
    bankName,
    last4,
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
    qualificationMethod,
    autoPaymentAgreementId,
  } = useLocalSearchParams<{
    bankName: string;
    last4: string;
    dueDate: string;
    feeAmount: string;
    amountId: string;
    advanceAmount: string;
    repaymentDate: string;
    selectedAccountId?: string;
    paymentSource?: LendingPaymentSource;
    selectedAccountName?: string;
    qualificationMethod: AdvanceQualificationMethod;
    autoPaymentAgreementId?: string;
  }>();

  const {
    result: backupPaymentMethods,
    fetching: fetchingBackupPayments,
    error: fetchingBackupPaymentsError,
  } = useBackupPayments();

  const handleLinkDebitCard = () => {
    router.navigate({
      pathname: '/move-money/tabapay/dcf-tabapay-form',
      params: {
        tabaPayEntryFlowType: TabaPayEntryFlowType.VaApplication,
        advanceAmount,
        dueDate,
        feeAmount,
        amountId,
        repaymentDate,
        selectedAccountId,
        paymentSource,
        selectedAccountName,
        qualificationMethod,
        autoPaymentAgreementId,
      },
    });
  };

  const openBottomSheet = () => {
    bottomSheetRef?.current?.present();
  };

  if (
    advanceAmount === undefined ||
    bankName === undefined ||
    last4 === undefined
  ) {
    return <Redirect href="/" />;
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={fetchingBackupPayments}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          fetchingBackupPaymentsError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('error.title')}
              messageBody={translatedText('error.body')}
              primaryButton={{
                title: translatedText('error.button'),
                onPress: () => router.dismissTo('/'),
              }}
            />
          ) : null
        }
      />

      <ActionScreen
        visual="DEBIT_CARD_A"
        titleText={translatedText('title', {
          advanceAmount: '$' + advanceAmount,
        })}
        titleTextStyle="extra-large"
        primaryButton={{
          label: translatedText('linkButton'),
          onPress: () => {
            handleLinkDebitCard();
          },
          analyticsId: 'continue',
          customAttributes: {
            cta: 'Continue',
          },
        }}
        bodyContent={
          <View style={styles.bodyContainer}>
            <BodyBaseText numberOfLines={0}>
              {translatedText('description', {
                bankName: bankName,
                last4: last4,
              })}
            </BodyBaseText>
            <BodyXSText style={styles.textColor} numberOfLines={0}>
              {translatedText('subDescription') + ' '}
              <HyperlinkXsText
                numberOfLines={1}
                onPress={openBottomSheet}
                analyticsId="learn-more"
                customAttributes={{ faq_name: 'link_debit_card' }}
              >
                {translatedText('link')}
              </HyperlinkXsText>
            </BodyXSText>
          </View>
        }
      />

      {!!backupPaymentMethods && backupPaymentMethods?.length > 0 ? (
        <BackUpPaymentsBottomSheet
          paymentOptions={backupPaymentMethods}
          ref={bottomSheetRef}
          analyticsObj={{
            id: 'backup-payment-options',
            customAttributes: {
              source: 'link_debit_card',
            },
          }}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContainer: {
    paddingBottom: theme.spacing.m,
    rowGap: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  textColor: {
    color: theme.color.text.secondary,
  },
});

export default LinkDebitCard;
